/**
 * El periodo de una pantalla con filtro de fecha. Es el primer filtro de fecha
 * del sistema (spec `2026-09-23-ventas-periodo-y-detalle-design.md` §1), asi que
 * la forma del valor se define aca y no adentro de la pantalla que lo estrena.
 *
 * POR QUE una union discriminada y no un `{desde, hasta}` pelado:
 *
 * - Un `{desde: "2026-09-01", hasta: "2026-09-30"}` ya no sabe que era
 *   "septiembre". Sin eso las flechas `◀ ▶` no tienen como moverse (¿suman 30
 *   dias? ¿29 en febrero?) y el rotulo no puede decir el nombre del mes: tendria
 *   que adivinarlo comparando contra el primero y el ultimo dia, y una vez que
 *   adivinas mal el usuario ve "01/09/26 – 30/09/26" donde esperaba SEPTIEMBRE.
 * - "Todo" no es un rango grande: es la ausencia de rango. Representarlo con
 *   fechas obliga a inventar un piso ("2000-01-01") que despues viaja a la query
 *   y filtra de verdad.
 *
 * Entonces: `mes` guarda anio y mes, `rango` guarda dos ISO y `todo` no guarda
 * nada. La traduccion a lo que entiende el backend es de una sola direccion y la
 * hace `rangoDelPeriodo`.
 *
 * ZONA HORARIA. Todo se arma con los getters locales del navegador
 * (`getFullYear`, `getMonth`, `getDate`), igual que `fechaDeHoy` de
 * `components/compras/CompraManual.tsx`. Nunca `toISOString()`: a la noche en
 * Buenos Aires (UTC-3) devuelve el dia siguiente, y "este mes" el 31 a las 22hs
 * pasaria a ser el mes que viene. Las fechas ISO tampoco se parsean con
 * `new Date("2026-09-01")`, que el estandar interpreta en UTC: se cortan con
 * `slice`, que es exacto y no depende de la zona.
 */

/** Mes 1-12. Se guarda 1-12 y no 0-11 como `Date`: el valor se lee en la URL y en los logs. */
export interface PeriodoMes {
  tipo: "mes";
  anio: number;
  mes: number;
}

/** Rango libre, ambas puntas en ISO `YYYY-MM-DD` y `hasta` INCLUSIVE. */
export interface PeriodoRango {
  tipo: "rango";
  desde: string;
  hasta: string;
}

/** Sin filtro de fecha. No viaja ningun parametro a la query. */
export interface PeriodoTodo {
  tipo: "todo";
}

export type Periodo = PeriodoMes | PeriodoRango | PeriodoTodo;

/**
 * Lo que entiende `GET /ventas`: `desde` y `hasta` en ISO, con `hasta`
 * inclusive (el backend hace `fecha < hasta + interval '1 day'`,
 * `rutas/ventas.ts:135-140`). Ambos opcionales porque "todo" no manda ninguno.
 */
export interface RangoDeQuery {
  desde?: string;
  hasta?: string;
}

const MESES = [
  "ENERO",
  "FEBRERO",
  "MARZO",
  "ABRIL",
  "MAYO",
  "JUNIO",
  "JULIO",
  "AGOSTO",
  "SEPTIEMBRE",
  "OCTUBRE",
  "NOVIEMBRE",
  "DICIEMBRE",
] as const;

const MESES_CORTOS = [
  "ENE",
  "FEB",
  "MAR",
  "ABR",
  "MAY",
  "JUN",
  "JUL",
  "AGO",
  "SEP",
  "OCT",
  "NOV",
  "DIC",
] as const;

// Los nombres se escriben a mano y no salen de `toLocaleDateString("es-AR")`:
// el resultado del ICU cambia entre Node, el navegador y la version del SO
// ("septiembre", "sept.", "set"), y este rotulo es el titulo visible del filtro.
const RE_ISO = /^\d{4}-\d{2}-\d{2}$/;

/** Una fecha ISO `YYYY-MM-DD` bien formada. No valida que el dia exista. */
export function esFechaISO(valor: string): boolean {
  return RE_ISO.test(valor);
}

/** Una fecha local como `YYYY-MM-DD`. Mismo criterio que `fechaDeHoy` de CompraManual. */
export function aISO(fecha: Date): string {
  const y = fecha.getFullYear();
  const m = String(fecha.getMonth() + 1).padStart(2, "0");
  const d = String(fecha.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Anio y mes (1-12) de una fecha ISO, sin pasar por `Date` (que parsearia en UTC). */
export function anioYMesDeISO(iso: string): { anio: number; mes: number } {
  return { anio: Number(iso.slice(0, 4)), mes: Number(iso.slice(5, 7)) };
}

/**
 * Ultimo dia del mes. `new Date(anio, mes, 0)` es "el dia 0 del mes siguiente",
 * o sea el ultimo del mes pedido, y el motor ya sabe de bisiestos: no hace falta
 * la regla de los 400 anios escrita a mano.
 */
export function ultimoDiaDelMes(anio: number, mes: number): number {
  return new Date(anio, mes, 0).getDate();
}

/** El mes al que pertenece una fecha. */
export function mesDe(fecha: Date): PeriodoMes {
  return { tipo: "mes", anio: fecha.getFullYear(), mes: fecha.getMonth() + 1 };
}

/** El mes corriente. Es el periodo con el que arranca la pantalla. */
/** El dia de hoy como rango de un dia (0155): Caja abre en lo que paso hoy. */
export function hoyComoRango(hoy: Date = new Date()): PeriodoRango {
  const iso = aISO(hoy);
  return { tipo: "rango", desde: iso, hasta: iso };
}

export function esteMes(hoy: Date = new Date()): PeriodoMes {
  return mesDe(hoy);
}

/**
 * El mes del periodo, sea cual sea su tipo: el ancla desde la que se mueven las
 * flechas. Un rango libre se ancla en el mes de su `desde` y "todo" en el mes
 * corriente, asi `◀` y `▶` siempre hacen algo en vez de quedar muertas.
 */
export function mesAncla(periodo: Periodo, hoy: Date = new Date()): PeriodoMes {
  if (periodo.tipo === "mes") return periodo;
  if (periodo.tipo === "rango") {
    const { anio, mes } = anioYMesDeISO(periodo.desde);
    return { tipo: "mes", anio, mes };
  }
  return esteMes(hoy);
}

/**
 * Corre el periodo `delta` meses. Cuenta en meses absolutos desde el anio 0 para
 * que diciembre + 1 sea enero del anio siguiente sin un `if` de fin de anio.
 * El resultado SIEMPRE es un `mes`: usar las flechas es elegir un mes, incluso
 * viniendo de un rango libre o de "todo".
 */
export function moverMes(periodo: Periodo, delta: number, hoy: Date = new Date()): PeriodoMes {
  const ancla = mesAncla(periodo, hoy);
  const absoluto = ancla.anio * 12 + (ancla.mes - 1) + delta;
  return { tipo: "mes", anio: Math.floor(absoluto / 12), mes: (absoluto % 12) + 1 };
}

/**
 * El periodo, traducido a lo que viaja en la query. Un mes se abre a sus dos
 * puntas aca y no en la pantalla, para que nadie vuelva a escribir el
 * `ultimoDiaDelMes` a mano.
 */
export function rangoDelPeriodo(periodo: Periodo): RangoDeQuery {
  if (periodo.tipo === "todo") return {};
  if (periodo.tipo === "rango") return { desde: periodo.desde, hasta: periodo.hasta };
  const mm = String(periodo.mes).padStart(2, "0");
  const ultimo = String(ultimoDiaDelMes(periodo.anio, periodo.mes)).padStart(2, "0");
  return { desde: `${periodo.anio}-${mm}-01`, hasta: `${periodo.anio}-${mm}-${ultimo}` };
}

/**
 * La vuelta: de dos parametros de query (o de la URL de la pantalla) al periodo.
 * Un rango que cae justo en las dos puntas de un mes vuelve a ser un `mes`, asi
 * recargar la pagina no degrada "SEPTIEMBRE 2026" a "01/09/26 – 30/09/26".
 *
 * Sin las dos puntas es "todo": el control nunca produce rangos abiertos, asi
 * que un `desde` suelto solo puede venir de una URL editada a mano.
 */
export function periodoDesdeRango(desde?: string | null, hasta?: string | null): Periodo {
  if (!desde || !hasta || !esFechaISO(desde) || !esFechaISO(hasta)) return { tipo: "todo" };
  if (rangoInvalido(desde, hasta)) return { tipo: "todo" };
  const { anio, mes } = anioYMesDeISO(desde);
  const esMesEntero =
    desde === rangoDelPeriodo({ tipo: "mes", anio, mes }).desde &&
    hasta === rangoDelPeriodo({ tipo: "mes", anio, mes }).hasta;
  return esMesEntero ? { tipo: "mes", anio, mes } : { tipo: "rango", desde, hasta };
}

/**
 * `desde` posterior a `hasta`. La comparacion es de strings porque el ISO
 * `YYYY-MM-DD` ordena igual alfabeticamente que cronologicamente; no hace falta
 * construir dos `Date`.
 */
export function rangoInvalido(desde: string, hasta: string): boolean {
  if (!esFechaISO(desde) || !esFechaISO(hasta)) return false;
  return desde > hasta;
}

/** `2026-09-15` a `15/09/26`, cortando el string (sin `Date`, sin zona horaria). */
export function fechaCorta(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}`;
}

/**
 * El rotulo del boton. `corto` existe por el riesgo 1 del spec: la barra de
 * filtros tiene que entrar en un solo renglon a 1280px y el mes largo
 * ("SEPTIEMBRE 2026") es lo primero que la parte en dos.
 */
export function rotuloPeriodo(periodo: Periodo, forma: "completo" | "corto" = "completo"): string {
  if (periodo.tipo === "todo") return forma === "corto" ? "TODO" : "TODO EL HISTORIAL";
  if (periodo.tipo === "mes") {
    const nombre = (forma === "corto" ? MESES_CORTOS : MESES)[periodo.mes - 1] ?? "";
    const anio = forma === "corto" ? String(periodo.anio).slice(2) : String(periodo.anio);
    return `${nombre} ${anio}`;
  }
  const desde = fechaCorta(periodo.desde);
  const hasta = fechaCorta(periodo.hasta);
  // Un solo dia no se escribe "15/03/26 – 15/03/26": se escribe una vez.
  return desde === hasta ? desde : `${desde} – ${hasta}`;
}

/** El mes de un periodo, en minuscula y con el anio: "octubre 2026". Para los aria-label. */
export function mesEnPalabras(periodo: PeriodoMes): string {
  const nombre = MESES[periodo.mes - 1] ?? "";
  return `${nombre.toLowerCase()} ${periodo.anio}`;
}

export type IdAtajo = "hoy" | "semana" | "mes" | "mes-pasado" | "todo";

export interface AtajoPeriodo {
  id: IdAtajo;
  etiqueta: string;
  periodo: Periodo;
}

/**
 * Los cinco atajos del popover, ya resueltos contra el reloj que se les pase.
 * Son datos y no JSX para poder probar en un test que "esta semana" arranca un
 * lunes y que "mes pasado" cruza el fin de anio.
 */
export function atajosPeriodo(hoy: Date = new Date()): AtajoPeriodo[] {
  const iso = aISO(hoy);
  // La semana rioplatense empieza el lunes: `getDay()` da 0 el domingo, asi que
  // se rota para que el lunes sea 0 y el domingo 6.
  const diaDeSemana = (hoy.getDay() + 6) % 7;
  const lunes = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - diaDeSemana);
  const domingo = new Date(lunes.getFullYear(), lunes.getMonth(), lunes.getDate() + 6);

  return [
    { id: "hoy", etiqueta: "Hoy", periodo: { tipo: "rango", desde: iso, hasta: iso } },
    {
      id: "semana",
      etiqueta: "Esta semana",
      periodo: { tipo: "rango", desde: aISO(lunes), hasta: aISO(domingo) },
    },
    { id: "mes", etiqueta: "Este mes", periodo: esteMes(hoy) },
    { id: "mes-pasado", etiqueta: "Mes pasado", periodo: moverMes(esteMes(hoy), -1, hoy) },
    { id: "todo", etiqueta: "Todo", periodo: { tipo: "todo" } },
  ];
}

/** Dos periodos que significan lo mismo. Sirve para marcar el atajo activo. */
export function mismoPeriodo(a: Periodo, b: Periodo): boolean {
  if (a.tipo !== b.tipo) return false;
  if (a.tipo === "todo") return true;
  if (a.tipo === "mes" && b.tipo === "mes") return a.anio === b.anio && a.mes === b.mes;
  if (a.tipo === "rango" && b.tipo === "rango") return a.desde === b.desde && a.hasta === b.hasta;
  return false;
}
